// src/services/adminService.js

import { Api } from "../Api";

/**
 * Admin service for managing organizations and users
 * Only accessible to users with is_admin=true
 */

// Organization Management
export const createCustomerOrganization = async (organizationData) => {
  try {
    const response = await Api.post("/admin/organizations", organizationData);
    return response.data;
  } catch (error) {
    console.error("Error creating customer organization:", error);
    throw error;
  }
};

export const getAllOrganizations = async () => {
  try {
    const response = await Api.get("/admin/organizations");
    return response.data;
  } catch (error) {
    console.error("Error fetching all organizations:", error);
    throw error;
  }
};

export const setOrganizationManagerHome = async (organizationId, managerHome) => {
  const response = await Api.patch(
    `/admin/organizations/${organizationId}/manager-home`,
    { manager_home: managerHome }
  );
  return response.data;
};

// User Management
export const createCustomerUser = async (userData) => {
  try {
    const response = await Api.post("/admin/users", userData);
    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const getAllUsers = async (organizationId = null) => {
  try {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await Api.get("/admin/users", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await Api.delete(`/admin/users/${userId}`);
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

export const updateUserAdmin = async (userId, userData) => {
  try {
    const response = await Api.patch(`/admin/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const setUserDefaultRole = async (userId, roleData) => {
  try {
    const response = await Api.put(`/users/${userId}/default-role`, roleData);
    return response.data;
  } catch (error) {
    console.error("Error updating user default role:", error);
    throw error;
  }
};

export const sendPasswordResetEmail = async (userId) => {
  try {
    const response = await Api.post(`/admin/users/${userId}/reset-password`, {});
    return response.data;
  } catch (error) {
    console.error("Error sending password reset:", error);
    throw error;
  }
};

export const getReportingGroups = async (organizationId) => {
  const response = await Api.get(`/organizations/${organizationId}/reporting-groups`);
  return response.data;
};

export const createReportingGroup = async (organizationId, groupData) => {
  const response = await Api.post(`/organizations/${organizationId}/reporting-groups`, groupData);
  return response.data;
};

export const updateReportingGroup = async (organizationId, groupId, groupData) => {
  const response = await Api.patch(`/organizations/${organizationId}/reporting-groups/${groupId}`, groupData);
  return response.data;
};

export const deleteReportingGroup = async (organizationId, groupId) => {
  await Api.delete(`/organizations/${organizationId}/reporting-groups/${groupId}`);
};

export const replaceReportingGroupMembers = async (organizationId, groupId, employeeUserIds) => {
  const response = await Api.put(
    `/organizations/${organizationId}/reporting-groups/${groupId}/members`,
    { employee_user_ids: employeeUserIds }
  );
  return response.data;
};

export const replaceReportingGroupManagerGrants = async (organizationId, groupId, managerGrants) => {
  const response = await Api.put(
    `/organizations/${organizationId}/reporting-groups/${groupId}/manager-grants`,
    { manager_grants: managerGrants }
  );
  return response.data;
};

// Bootstrap Management
export const getBootstrapStatus = async () => {
  try {
    const response = await Api.get("/bootstrap/status");
    return response.data;
  } catch (error) {
    console.error("Error fetching bootstrap status:", error);
    throw error;
  }
};

export const bootstrapCreateOrganization = async () => {
  try {
    const response = await Api.post("/bootstrap/create-organization");
    return response.data;
  } catch (error) {
    console.error("Error creating ethosphere organization:", error);
    throw error;
  }
};

export const bootstrapCreateAdminUser = async () => {
  try {
    const response = await Api.post("/bootstrap/create-admin-user");
    return response.data;
  } catch (error) {
    console.error("Error creating admin user:", error);
    throw error;
  }
};

export const bootstrapComplete = async () => {
  try {
    const response = await Api.post("/bootstrap/complete");
    return response.data;
  } catch (error) {
    console.error("Error completing bootstrap:", error);
    throw error;
  }
};

// Signature Management
export const getAllSignatures = async (organizationId = null, userId = null) => {
  try {
    const params = {};
    if (organizationId) params.organization_id = organizationId;
    if (userId) params.user_id = userId;
    const response = await Api.get("/admin/signatures", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching signatures:", error);
    throw error;
  }
};

export const getSignatureWithUser = async (signatureId) => {
  try {
    const response = await Api.get(`/admin/signatures/${signatureId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching signature details:", error);
    throw error;
  }
};

export const updateSignatureAdmin = async (signatureId, signatureData) => {
  try {
    const response = await Api.put(
      `/admin/signatures/${signatureId}`,
      signatureData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating signature:", error);
    throw error;
  }
};

export const getSignatureAudioUrlAdmin = async (signatureId) => {
  try {
    const response = await Api.get(`/admin/signatures/${signatureId}/audio`);
    return response.data;
  } catch (error) {
    console.error("Error fetching signature audio:", error);
    throw error;
  }
};

// Session Management
export const getAdminSessions = async (params = {}) => {
  try {
    const response = await Api.get("/admin/sessions", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching sessions:", error);
    throw error;
  }
};

export const getAdminSession = async (sessionId) => {
  try {
    const response = await Api.get(`/admin/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching session details:", error);
    throw error;
  }
};

export const deactivateAdminSession = async (sessionId) => {
  try {
    const response = await Api.put(`/admin/sessions/${sessionId}/deactivate`);
    return response.data;
  } catch (error) {
    console.error("Error deactivating session:", error);
    throw error;
  }
};

// Thread Management
export const getAdminThreads = async (params = {}) => {
  try {
    const response = await Api.get("/admin/threads", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching admin threads:", error);
    throw error;
  }
};

export const getAdminThread = async (threadId) => {
  try {
    const response = await Api.get(`/admin/threads/${threadId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching admin thread:", error);
    throw error;
  }
};

export const triggerAdminThreadReanalyze = async (threadId) => {
  try {
    const response = await Api.post(`/admin/threads/${threadId}/reanalyze`, {});
    return response.data;
  } catch (error) {
    console.error("Error reanalyzing admin thread:", error);
    throw error;
  }
};

export const triggerAdminConversationRetag = async (conversationId) => {
  try {
    const response = await Api.post(`/admin/conversations/${conversationId}/retag`, {});
    return response.data;
  } catch (error) {
    console.error("Error retagging admin conversation:", error);
    throw error;
  }
};

export const triggerAdminConversationRestitch = async (conversationId) => {
  try {
    const response = await Api.post(
      `/admin/conversations/${conversationId}/heal-stitcher-ordinals`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error("Error restitching admin conversation:", error);
    throw error;
  }
};
